import React, { useState } from 'react';
import axios from 'axios';
import { Users, Heart, Shield, UserCheck, Briefcase, FileText, TrendingUp, Activity, Mail, Phone, MapPin, Calendar, Clock, Send } from 'lucide-react';
import { services, companyInfo } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const iconMap = {
  Users,
  Heart,
  Shield,
  UserCheck,
  Briefcase,
  FileText,
  TrendingUp,
  Activity
};

const Home = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [appointmentForm, setAppointmentForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    time: '',
    notes: ''
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      
      if (response.data.success) {
        toast.success("Mensaje enviado", {
          description: "Gracias por contactarnos. Le responderemos pronto.",
        });
        setContactForm({ name: '', email: '', phone: '', message: '' });
      } else {
        toast.error("Error", {
          description: response.data.message || "Hubo un problema al enviar el mensaje.",
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error("Error", {
        description: "No se pudo enviar el mensaje. Por favor, intente nuevamente.",
      });
    }
  };

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API}/appointments`, appointmentForm);
      
      if (response.data.success) {
        toast.success("Cita agendada", {
          description: "Su cita ha sido registrada. Le confirmaremos por email.",
        });
        setAppointmentForm({ name: '', email: '', phone: '', service: '', date: '', time: '', notes: '' });
      } else {
        toast.error("Error", {
          description: response.data.message || "Hubo un problema al agendar la cita.",
        });
      }
    } catch (error) {
      console.error('Error submitting appointment form:', error);
      toast.error("Error", {
        description: "No se pudo agendar la cita. Por favor, intente nuevamente.",
      });
    }
  };

  return (
    <div className="dark-container">
      {/* Header */}
      <header className="dark-header">
        <img 
          src="https://customer-assets.emergentagent.com/job_hr-experts/artifacts/y4e6ejx3_Logo%20AZ%20Consulting.png" 
          alt="AZ Consulting" 
          className="dark-logo"
        />
        <nav className="dark-nav">
          <a href="#inicio" className="dark-nav-link">Inicio</a>
          <a href="#servicios" className="dark-nav-link">Servicios</a>
          <a href="#citas" className="dark-nav-link">Agendar Cita</a>
          <a href="#contacto" className="dark-nav-link">Contacto</a>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="inicio" className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="display-huge">Transformamos el talento humano de su empresa</h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginTop: '24px' }}>
              Con más de {companyInfo.experience} años de experiencia en consultoría de recursos humanos, 
              brindamos soluciones estratégicas para optimizar la gestión del talento en su organización.
            </p>
            <div style={{ display: 'flex', gap: '16px', marginTop: '40px' }}>
              <a href="#citas">
                <Button className="btn-primary">Agendar Consulta</Button>
              </a>
              <a href="#servicios">
                <Button className="btn-secondary">Ver Servicios</Button>
              </a>
            </div>
          </div>
          <div className="hero-image">
            <img 
              src="https://images.unsplash.com/photo-1758518731457-5ef826b75b3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHRlYW18ZW58MHx8fHwxNzY4NDQ5MzkyfDA&ixlib=rb-4.1.0&q=85" 
              alt="Equipo profesional"
              style={{ width: '100%', height: '500px', objectFit: 'cover', borderRadius: '0px' }}
            />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="services-section">
        <h2 className="display-large" style={{ textAlign: 'center', marginBottom: '60px' }}>Nuestros Servicios</h2>
        <div className="dark-grid">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card key={service.id} className="service-card">
                <CardContent style={{ padding: '40px' }}>
                  <div className="service-icon">
                    <IconComponent size={48} strokeWidth={1.5} style={{ color: 'var(--brand-primary)' }} />
                  </div>
                  <h3 className="heading-2" style={{ marginTop: '24px', marginBottom: '16px' }}>{service.title}</h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>{service.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Appointment Section */}
      <section id="citas" className="appointment-section">
        <div className="appointment-container">
          <div className="appointment-info">
            <h2 className="display-large">Agende su Consulta</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginTop: '24px' }}>
              Programe una reunión con nuestros especialistas en recursos humanos. 
              Analizaremos sus necesidades y le proporcionaremos una propuesta personalizada.
            </p>
            <div style={{ marginTop: '40px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Clock size={24} style={{ color: 'var(--brand-primary)' }} />
                <span className="body-medium">Consultas de 60 minutos</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Calendar size={24} style={{ color: 'var(--brand-primary)' }} />
                <span className="body-medium">Disponibilidad de lunes a viernes</span>
              </div>
            </div>
          </div>
          <div className="appointment-form-container">
            <form onSubmit={handleAppointmentSubmit} className="contact-form">
              <Input 
                placeholder="Nombre completo"
                value={appointmentForm.name}
                onChange={(e) => setAppointmentForm({...appointmentForm, name: e.target.value})}
                required
                className="form-input"
              />
              <Input 
                type="email"
                placeholder="Email"
                value={appointmentForm.email}
                onChange={(e) => setAppointmentForm({...appointmentForm, email: e.target.value})}
                required
                className="form-input"
              />
              <Input 
                type="tel"
                placeholder="Teléfono"
                value={appointmentForm.phone}
                onChange={(e) => setAppointmentForm({...appointmentForm, phone: e.target.value})}
                required
                className="form-input"
              />
              <select
                value={appointmentForm.service}
                onChange={(e) => setAppointmentForm({...appointmentForm, service: e.target.value})}
                required
                className="form-select"
              >
                <option value="">Seleccione un servicio</option>
                {services.map(service => (
                  <option key={service.id} value={service.title}>{service.title}</option>
                ))}
              </select>
              <Input 
                type="date"
                value={appointmentForm.date}
                onChange={(e) => setAppointmentForm({...appointmentForm, date: e.target.value})}
                required
                className="form-input"
              />
              <Input 
                type="time"
                value={appointmentForm.time}
                onChange={(e) => setAppointmentForm({...appointmentForm, time: e.target.value})}
                required
                className="form-input"
              />
              <Textarea 
                placeholder="Comentarios adicionales (opcional)"
                value={appointmentForm.notes}
                onChange={(e) => setAppointmentForm({...appointmentForm, notes: e.target.value})}
                className="form-textarea"
                rows={4}
              />
              <Button type="submit" className="btn-primary" style={{ width: '100%' }}>
                Agendar Cita
                <Calendar size={20} />
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="contact-section">
        <div className="contact-container">
          <div className="contact-form-container">
            <h2 className="display-large">Contáctenos</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginTop: '16px', marginBottom: '40px' }}>
              ¿Tiene preguntas? Envíenos un mensaje y le responderemos a la brevedad.
            </p>
            <form onSubmit={handleContactSubmit} className="contact-form">
              <Input 
                placeholder="Nombre completo"
                value={contactForm.name}
                onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                required
                className="form-input"
              />
              <Input 
                type="email"
                placeholder="Email"
                value={contactForm.email}
                onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                required
                className="form-input"
              />
              <Input 
                type="tel"
                placeholder="Teléfono"
                value={contactForm.phone}
                onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                required
                className="form-input"
              />
              <Textarea 
                placeholder="Su mensaje"
                value={contactForm.message}
                onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                required
                className="form-textarea"
                rows={6}
              />
              <Button type="submit" className="btn-primary" style={{ width: '100%' }}>
                Enviar Mensaje
                <Send size={20} />
              </Button>
            </form>
          </div>
          <div className="contact-info">
            <h3 className="heading-1" style={{ marginBottom: '40px' }}>Información de Contacto</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              <div style={{ display: 'flex', gap: '20px' }}>
                <Mail size={28} style={{ color: 'var(--brand-primary)', flexShrink: 0 }} />
                <div>
                  <p className="body-small" style={{ color: 'var(--text-muted)', marginBottom: '8px' }}>Email</p>
                  <a href={`mailto:${companyInfo.email}`} className="body-medium" style={{ color: 'var(--text-primary)' }}>
                    {companyInfo.email}
                  </a>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '20px' }}>
                <Phone size={28} style={{ color: 'var(--brand-primary)', flexShrink: 0 }} />
                <div>
                  <p className="body-small" style={{ color: 'var(--text-muted)', marginBottom: '8px' }}>Teléfono</p>
                  <a href={`tel:${companyInfo.phone}`} className="body-medium" style={{ color: 'var(--text-primary)' }}>
                    {companyInfo.phone}
                  </a>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '20px' }}>
                <MapPin size={28} style={{ color: 'var(--brand-primary)', flexShrink: 0 }} />
                <div>
                  <p className="body-small" style={{ color: 'var(--text-muted)', marginBottom: '8px' }}>Dirección</p>
                  <p className="body-medium" style={{ color: 'var(--text-primary)' }}>
                    {companyInfo.address}
                  </p>
                </div>
              </div>
            </div>
            <div style={{ marginTop: '60px' }}>
              <img 
                src="https://images.unsplash.com/photo-1758518727653-5650fd9e146c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxIUiUyMGNvbnN1bHRpbmd8ZW58MHx8fHwxNzY4NDQ5Mzk4fDA&ixlib=rb-4.1.0&q=85" 
                alt="Consultoría profesional"
                style={{ width: '100%', height: '300px', objectFit: 'cover', borderRadius: '0px' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="dark-footer">
        <div className="footer-content">
          <div className="footer-section">
            <img 
              src="https://customer-assets.emergentagent.com/job_hr-experts/artifacts/y4e6ejx3_Logo%20AZ%20Consulting.png" 
              alt="AZ Consulting" 
              style={{ height: '70px', objectFit: 'contain', marginBottom: '20px' }}
            />
            <p className="body-small" style={{ color: 'var(--text-muted)' }}>
              Más de {companyInfo.experience} años transformando el talento humano de las organizaciones.
            </p>
          </div>
          <div className="footer-section">
            <h4 className="heading-3" style={{ marginBottom: '20px' }}>Servicios</h4>
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <li><a href="#servicios" className="body-small" style={{ color: 'var(--text-muted)' }}>Team Building</a></li>
              <li><a href="#servicios" className="body-small" style={{ color: 'var(--text-muted)' }}>Inteligencia Emocional</a></li>
              <li><a href="#servicios" className="body-small" style={{ color: 'var(--text-muted)' }}>Selección de Personal</a></li>
              <li><a href="#servicios" className="body-small" style={{ color: 'var(--text-muted)' }}>Clima Laboral</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4 className="heading-3" style={{ marginBottom: '20px' }}>Contacto</h4>
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <li className="body-small" style={{ color: 'var(--text-muted)' }}>{companyInfo.email}</li>
              <li className="body-small" style={{ color: 'var(--text-muted)' }}>{companyInfo.phone}</li>
              <li className="body-small" style={{ color: 'var(--text-muted)' }}>{companyInfo.address}</li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="body-small" style={{ color: 'var(--text-muted)', textAlign: 'center' }}>
            © 2025 AZ Consulting. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;